/**
 * Slice 02 of wps-preview-pixel-parity.
 *
 * Pure helper that converts the typed `cssVars` token table (from
 * `wpsPreviewTheme`) into a `React.CSSProperties` object suitable
 * for an inline `style={}` prop on the top-level canvas wrapper.
 *
 * Applied once on `WpsPreviewCanvas`'s outer `<div>` so every
 * descendant cell can read tokens via Tailwind arbitrary-value
 * syntax (`border-[length:var(--wps-hairline-width)]`,
 * `bg-[color:var(--wps-brand-red)]`, …) without duplicating literals.
 *
 * The function is intentionally pure + unit-testable so a future
 * regression that drops a token from the wrapper shows up as a
 * test failure, not as silently-broken CSS at runtime.
 */

import type { cssVars } from './wpsPreviewTheme'
import type { CSSProperties } from 'react'

type WpsCssVarsMap = typeof cssVars

/**
 * Convert a `cssVars` map (typed `--wps-*` keys → string values)
 * into a `React.CSSProperties` object. Output keys are the same
 * `--wps-*` strings; values are unchanged.
 *
 * `React.CSSProperties` does not type custom-property keys so we
 * cast through `Record<string, string>` and back. This is the
 * canonical pattern for emitting CSS custom properties from React
 * inline styles.
 */
export function wpsPreviewCssVars(theme: WpsCssVarsMap): CSSProperties {
  const out: Record<string, string> = {}
  for (const key of Object.keys(theme) as Array<keyof WpsCssVarsMap>) {
    out[key] = theme[key]
  }
  return out as CSSProperties
}
